import React from 'react'
import styled from 'styled-components'
import {Info} from 'lucide-react'
import {Tooltip, type TooltipProps} from '../BaseComponents'

/**
 * InfoHint
 *
 * The info trigger beside a caption, heading or value: a button named by `label` whose
 * explanation (a definition, a comparison window, why a value reads '—') is the DS Tooltip's
 * content. Hover or focus shows it, and it is always the button's accessible description. It is
 * also a toggletip, for touch: a click or Enter pins it open, and doing it again, Escape or a
 * click outside closes it.
 *
 * The trigger is the Info icon, or `children` in its place (an uncomputable delta's '—').
 *
 * @example
 *   <InfoHint label="About Fill ratio" content={definition} data-testid={`${cellTestId}-info`} />
 *   <InfoHint label="Change from the previous period" content={reason}>—</InfoHint>
 */

export type InfoHintSize = 'sm' | 'md'

export interface InfoHintProps {
  /** The explanation (definition, comparison window, why a value is '—'). No interactive content. */
  content: React.ReactNode
  /** The trigger's accessible name, e.g. "About Fill ratio". Required: the icon has no text. */
  label: string
  side?: TooltipProps['side']
  /** The icon: 14px (sm, the default) or 16px (md). The target is at least 24 × 24 either way. */
  size?: InfoHintSize
  /** Visible trigger content in place of the icon, e.g. the '—' of an uncomputable delta. */
  children?: React.ReactNode
  /** On the button. The matrix pattern is `${cellTestId}-info`. */
  'data-testid'?: string
}

const ICON_SIZE: Record<InfoHintSize, number> = {sm: 14, md: 16}

const Trigger = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  vertical-align: middle;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: var(--radius-pill);
  color: var(--text-secondary);
  font: inherit;
  cursor: pointer;

  /* A target of at least 24 × 24 (WCAG 2.5.8) that doesn't grow the line the hint sits in. */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    min-width: var(--space-6);
    min-height: var(--space-6);
    transform: translate(-50%, -50%);
  }

  &:hover {
    color: var(--text-link);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
`

export function InfoHint({
  content,
  label,
  side = 'top',
  size = 'sm',
  children,
  'data-testid': testId,
}: InfoHintProps) {
  // The button is named by `label`, not by its text, so the content is always its description.
  return (
    <Tooltip content={content} side={side} describeChild toggleOnClick>
      <Trigger type="button" aria-label={label} data-testid={testId}>
        {children ?? <Info size={ICON_SIZE[size]} aria-hidden />}
      </Trigger>
    </Tooltip>
  )
}
